import { defineStore } from 'pinia'
import axios from 'axios'

const API_URL = '/api'
const LAST_LIST_KEY = 'lastOpenedListId'

export const useListStore = defineStore('list', {
  state: () => ({
    lists: [],
    currentList: null,
    loading: false,
    showCheckedItems: true, // Controls visibility of checked items across views
    // Shared link state
    sharedLinkName: null, // Name of the current shared link
    // Print trigger state
    triggerPrint: false // Flag to trigger print dialog from external components
  }),
  
  getters: {
    // Helper to get the last opened list ID from localStorage
    getLastOpenedListId() {
      try {
        return localStorage.getItem(LAST_LIST_KEY)
      } catch (error) {
        console.warn('Failed to read from localStorage:', error)
        return null
      }
    }
  },
  
  actions: {
    async fetchLists() {
      this.loading = true
      try {
        const response = await axios.get(`${API_URL}/packlists`)
        this.lists = response.data
        
        if (this.lists.length > 0 && !this.currentList) {
          // Try to load the last opened list first
          const lastOpenedId = this.getLastOpenedListId
          let selectedList = null
          
          if (lastOpenedId) {
            selectedList = this.lists.find(p => p.id === parseInt(lastOpenedId))
          }
          
          // Fallback to newest list if last opened doesn't exist
          if (!selectedList) {
            selectedList = this.lists[this.lists.length - 1]
          }
          
          this.setCurrentList(selectedList)
        }
      } catch (error) {
        console.error('Failed to fetch lists:', error)
      } finally {
        this.loading = false
      }
    },
    
    async createList(listData) {
      try {
        const response = await axios.post(`${API_URL}/packlists`, listData)
        this.lists.push(response.data)
        this.currentList = response.data
        return { success: true, list: response.data }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Failed to create list' 
        }
      }
    },
    
    async fetchList(listId) {
      try {
        const response = await axios.get(`${API_URL}/packlists/${listId}`)
        const list = response.data
        
        // Update the list in the lists array
        const index = this.lists.findIndex(p => p.id === listId)
        if (index !== -1) {
          this.lists[index] = list
        }
        
        // Update current list if it's the same one
        if (this.currentList?.id === listId) {
          this.currentList = list
        }
        
        return { success: true, list }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Failed to fetch list' 
        }
      }
    },

    async updateList(listId, listData) {
      try {
        const response = await axios.put(`${API_URL}/packlists/${listId}`, listData)
        const index = this.lists.findIndex(p => p.id === listId)
        if (index !== -1) {
          this.lists[index] = response.data
        }
        if (this.currentList?.id === listId) {
          this.currentList = response.data
        }
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Failed to update list' 
        }
      }
    },
    
    async deleteList(listId) {
      try {
        await axios.delete(`${API_URL}/packlists/${listId}`)
        this.lists = this.lists.filter(p => p.id !== listId)
        if (this.currentList?.id === listId) {
          // Clear localStorage if the deleted list was the stored one
          const lastOpenedId = this.getLastOpenedListId
          if (lastOpenedId === listId.toString()) {
            try {
              localStorage.removeItem(LAST_LIST_KEY)
            } catch (error) {
              console.warn('Failed to clear localStorage:', error)
            }
          }
          
          // Set new current list (this will also update localStorage)
          const newCurrent = this.lists.length > 0 ? this.lists[0] : null
          this.setCurrentList(newCurrent)
        }
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Failed to delete list' 
        }
      }
    },
    
    async addItem(listId, itemData) {
      try {
        const response = await axios.post(`${API_URL}/packlists/${listId}/items`, itemData)
        if (this.currentList?.id === listId) {
          this.currentList.items.push(response.data)
        }
        return { success: true, item: response.data }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Failed to add item' 
        }
      }
    },
    
    async updateItem(itemId, itemData) {
      try {
        const response = await axios.put(`${API_URL}/items/${itemId}`, itemData)
        if (this.currentList) {
          const index = this.currentList.items.findIndex(i => i.id === itemId)
          if (index !== -1) {
            this.currentList.items[index] = response.data
          }
        }
        return { success: true, item: response.data }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Failed to update item' 
        }
      }
    },
    
    async deleteItem(itemId) {
      try {
        await axios.delete(`${API_URL}/items/${itemId}`)
        if (this.currentList) {
          this.currentList.items = this.currentList.items.filter(i => i.id !== itemId)
        }
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Failed to delete item' 
        }
      }
    },
    
    async reorderItems(listId, itemIds) {
      try {
        await axios.put(`${API_URL}/packlists/${listId}/reorder`, itemIds)
        
        // Update local state to reflect new order
        if (this.currentList && this.currentList.id === listId) {
          // Create a map for quick item lookup
          const itemMap = new Map(this.currentList.items.map(item => [item.id, item]))
          
          // Reorder the items array based on the new order
          this.currentList.items = itemIds.map((id, index) => {
            const item = itemMap.get(id)
            if (item) {
              // Update the order_index to match the new position
              item.order_index = index
            }
            return item
          }).filter(Boolean) // Remove any undefined items
        }
        
        return { success: true }
      } catch (error) {
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Failed to reorder items' 
        }
      }
    },
    
    setCurrentList(list) {
      this.currentList = list
      
      // Save the selected list ID to localStorage
      if (list && list.id) {
        try {
          localStorage.setItem(LAST_LIST_KEY, list.id.toString())
        } catch (error) {
          console.warn('Failed to save to localStorage:', error)
        }
      }
    },

    toggleShowCheckedItems() {
      this.showCheckedItems = !this.showCheckedItems
    },

    // Shared link actions
    setSharedLinkName(name) {
      this.sharedLinkName = name
    },

    clearSharedLinkName() {
      this.sharedLinkName = null
    },

    // Print trigger actions
    triggerPrintDialog() {
      this.triggerPrint = true
    },

    resetPrintTrigger() {
      this.triggerPrint = false
    }
  }
})